import type { ShortlistAgent } from "@/services/shortlist-service";
import { Input } from "@/components/ui/input";
import icBolt from "@/assets/icons/bolt.svg";

interface AgentCardProps {
  agent: ShortlistAgent;
  index: number;
  isSelected: boolean;
  onToggleSelect: (agentId: string) => void;
  onView: () => void;
}

export function AgentCard({
  agent,
  index,
  isSelected,
  onToggleSelect,
  onView,
}: AgentCardProps) {
  const visibleCategories = agent.categories?.slice(0, 6) ?? [];
  const overflowCount = (agent.categories?.length ?? 0) - 6;

  return (
    <div
      className={`flex flex-col p-[4px] rounded-[8px] border transition-all bg-[#FF80131C] ${
        isSelected
          ? "border-[#FF3B00] bg-[#FFF9F5]"
          : "border-[#F5F5F5] bg-white hover:border-[#FF3B00]/50"
      }`}
    >
      {/* Card Header: Number Badge + Label + Checkbox */}
      <div className="flex items-center gap-[12px] py-[16px] px-[18px]">
        <div
          className="flex items-center justify-center w-[20px] h-[20px] rounded-full text-[14px]"
          style={{
            backgroundColor: isSelected ? "#FF3B00" : "#202020",
            color: "white",
          }}
        >
          {index}
        </div>
        <h4 className="flex-1 text-[14px] font-normal text-[#8D8D8D] truncate">
          {index === 1 && "Excellent match with your needs"}
        </h4>
        <Input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(agent.agent_id)}
          className="w-5 h-5 rounded border border-black/[0.15] accent-[#FF3B00] cursor-pointer bg-transparent p-0"
        />
      </div>

      {/* Agent Info */}
      <div className="flex flex-col flex-1 gap-[12px] bg-white rounded-[12px] p-[20px]">
        <div className="flex items-center gap-3">
          <span className="text-caption text-[#646464]">
            By {agent.provider}
          </span>
          <div className="w-[1px] h-[11px] bg-black/15" />
          <div className="flex items-center gap-1">
            <img src={icBolt} alt="bolt" />
            <span className="text-caption text-[#646464]">
              {agent.selected_count} usage left
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-[4px] pb-[20px] border-b border-[#F5F5F5]">
          <p
            className="text-[16px] text-[#202020] font-bold truncate"
            title={agent.name}
          >
            {agent.name}
          </p>
          <p
            className="text-[14px] text-[#202020] truncate"
            title={agent.short_description}
          >
            {agent.short_description}
          </p>
        </div>

        {/* Categories Tags */}
        <div className="flex flex-wrap gap-[8px]">
          {visibleCategories.map((category, i) => (
            <span
              key={i}
              className="px-[8px] py-[4px] text-[12px] text-[#646464] bg-[#F9F9F9] rounded-[4px] truncate max-w-[150px]"
              title={category}
            >
              {category}
            </span>
          ))}
          {overflowCount > 0 && (
            <span className="px-[8px] py-[4px] text-[12px] text-[#8D8D8D] bg-[#F9F9F9] rounded-[4px]">
              +{overflowCount} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
}